/**
 * React hook that handles landing cookie data which was migrated to local storage
 */
import Cookies from 'js-cookie';
import { useEffect } from 'react';

// Utilities.
import getLocalStorage from '../../../utilities/getLocalStorage';
import parseJsonString from '../../../utilities/parseJsonString';
import removeLocalStorage from '../../../utilities/removeLocalStorage';
import setLocalStorage from '../../../utilities/setLocalStorage';

const DATA_KEYS = [`seq_landing_referrer`, `seq_landing_url`];

const MILISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const EXPIRATION_DURATIONS = {
	'24h': MILISECONDS_PER_DAY,
	'28d': 28 * MILISECONDS_PER_DAY,
	'10y': 3650 * MILISECONDS_PER_DAY,
};

const POSSIBLE_DATA_KEYS = DATA_KEYS.map((dataKey) =>
	Object.keys(EXPIRATION_DURATIONS).map((expirationDuration) => `${dataKey}_${expirationDuration}`)
).flat();

const removeIncorrectLocalStorageData = () => {
	for (var i = 0; i < localStorage.length; i++) {
		const localStorageValueKey = localStorage.key(i);
		if (POSSIBLE_DATA_KEYS.includes(localStorageValueKey)) {
			const localStorageValueParsed = parseJsonString(getLocalStorage(localStorageValueKey));
			const isLocalStorageValueCorrect =
				typeof localStorageValueParsed === `object` &&
				Object.prototype.hasOwnProperty.call(localStorageValueParsed, `expires`) &&
				Object.prototype.hasOwnProperty.call(localStorageValueParsed, `value`) &&
				Object.keys(localStorageValueParsed).length === 2;

			if (!isLocalStorageValueCorrect) {
				removeLocalStorage(localStorageValueKey);
			}
		}
	}
};

const removeExpiredLocalStorageData = () => {
	const currentDate = new Date();
	for (var i = 0; i < localStorage.length; i++) {
		const localStorageValueKey = localStorage.key(i);
		if (POSSIBLE_DATA_KEYS.includes(localStorageValueKey)) {
			const localStorageValueParsed = parseJsonString(getLocalStorage(localStorage.key(i)));
			if (!!localStorageValueParsed && Object.prototype.hasOwnProperty.call(localStorageValueParsed, `expires`)) {
				const expiryDate = new Date(localStorageValueParsed.expires);
				if (currentDate.getTime() > expiryDate.getTime()) {
					removeLocalStorage(localStorageValueKey);
				}
			}
		}
	}
};

const setLocalStorageDataEntry = (dataKey, durationKey, value) => {
	const currentDate = new Date();
	setLocalStorage(
		dataKey,
		JSON.stringify({
			value,
			expires: new Date(EXPIRATION_DURATIONS[durationKey] + currentDate.getTime()).toISOString(),
		})
	);
};

const setLocalStorageData = (key, value) => {
	Object.keys(EXPIRATION_DURATIONS).forEach((durationKey) => {
		const dataKey = `${key}_${durationKey}`;
		// Migrate existing cookies to local storage entries
		if (Cookies.get(dataKey)) {
			Cookies.remove(dataKey);
			setLocalStorageDataEntry(dataKey, durationKey, value);
		}
		if (!getLocalStorage(dataKey)) {
			setLocalStorageDataEntry(dataKey, durationKey, value);
		}
	});
};

const setApplicationStorageData = (location) => {
	removeIncorrectLocalStorageData();
	removeExpiredLocalStorageData();
	const DATA_NAME_REFERRER = `seq_landing_referrer`;
	const DATA_NAME_URL = `seq_landing_url`;

	const amItheReferrer = document.referrer
		.replace(`http://`, ``)
		.replace(`https://`, ``)
		.split(`/`)[0]
		.match(new RegExp(`.*` + location.host.replace(`www.`, ``)));

	if (amItheReferrer) {
		return;
	}

	try {
		setLocalStorageData(DATA_NAME_REFERRER, document.referrer || location.origin);
	} catch (error) {
		console.error(error);
	}

	try {
		setLocalStorageData(DATA_NAME_URL, `${location.pathname}${location.search}`);
	} catch (error) {
		console.error(error);
	}
};

const useLandingDataLocalStorageHandling = (location) => {
	useEffect(() => {
		setApplicationStorageData(location);
	}, [location]);
};

export { POSSIBLE_DATA_KEYS };
export default useLandingDataLocalStorageHandling;
